import * as documentActions from "@rentiohq/shared-frontend/dist/reduxV2/documents/document.actions";
import * as documentHooks from "@rentiohq/shared-frontend/dist/reduxV2/documents/document.hooks";
import { getDocumentCategoryName } from "@rentiohq/shared-frontend/dist/reduxV2/documents/document.utils";
import * as propertyHooks from "@rentiohq/shared-frontend/dist/reduxV2/property/property.hooks";
import * as propertySelectors from "@rentiohq/shared-frontend/dist/reduxV2/property/property.selectors";
import { taskModule } from "@rentiohq/shared-frontend/dist/reduxV2/task";
import * as templateDocumentActions from "@rentiohq/shared-frontend/dist/reduxV2/templateDocuments/templateDocument.actions";
import { formatAddress } from "@rentiohq/shared-frontend/dist/utils/address.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { formatBytes } from "@rentiohq/shared-frontend/dist/utils/number.utils";
import { check } from "@rentiohq/web-shared/dist/components";
import utils from "@rentiohq/web-shared/dist/utils";
import { EAssets } from "@rentiohq/web-shared-next/dist/assets";
import { SHOW_CONFIRM_MODAL } from "@rentiohq/web-shared-next/dist/redux/actions";
import {
  ETagVariant,
  Icon,
  IMenuAction,
  SpinningLoader,
  Stack,
  Tag,
  Typography,
} from "@rentiohq/web-shared-next/dist/ui-components";
import { EButtonVariants } from "@rentiohq/web-shared-next/dist/utils/types/button";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { IRootStore } from "redux/reducers";
import { ts } from "services";
import { PropertyIcon } from "utils/property.utils";
import { AddDocumentModal } from "../../../../components/Modals/AddDocument";
import { DocumentInfo } from "./components/DocumentInfo";
import { DocumentMetaStyles } from "./Document.styles";

export enum EDocumentPackageTabs {
  Info,
  History,
}

interface IProps {
  documentId: string;
}

export const Document = ({ documentId }: IProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const sx = DocumentMetaStyles();

  const [showEdit, setShowEdit] = React.useState(false);

  // const documentPackageIdentifier = `document-package-identifier-${documentId}`;

  const { detail: document, isFetching } = documentHooks.useDetail({
    shouldRefetch: false,
    id: documentId,
  });

  const property = useSelector((state: IRootStore) =>
    propertySelectors.getDetail(state, document?.propertyId),
  );

  const { detail: linkedProperty } = propertyHooks.useDetail({
    shouldRefetch: false,
    id: document?.propertyId,
  });

  const linkedTask = useSelector((state: IRootStore) =>
    document?.taskId
      ? taskModule.selectors.getDetail(state, document.taskId)
      : undefined,
  );

  const documentTypeData = document
    ? utils.document.getTypeData(document.mimeType)
    : undefined;

  const canEditDocument = document
    ? check(document.roles, "document:update")
    : false;
  const canDeleteDocument = document
    ? check(document.roles, "document:delete")
    : false;

  React.useEffect(() => {
    if (isFetching) {
      return;
    }

    dispatch(
      documentActions.getDetailStart.getAction({
        id: documentId,
      }),
    );
  }, [documentId]);

  const handleEdit = (values: any) => {
    if (!document) {
      return;
    }

    dispatch(
      documentActions.updateStart.getAction({
        id: document.id,
        data: values,
      }),
    );

    setShowEdit(false);
  };

  // Render
  if (!document || !documentTypeData) {
    if (isFetching) {
      return <SpinningLoader />;
    }

    return null;
  }

  const mainActions: IMenuAction[] = [];
  if (document.url) {
    mainActions.push({
      label: getLocalizedText("document.view"),
      onClick: () => utils.document.viewDocument(document),
    });
  }
  if (document.downloadUrl) {
    mainActions.push({
      label: getLocalizedText("document.download"),
      onClick: () => {
        if (document.downloadUrl) {
          window.location.href = document.downloadUrl;
        }
      },
    });
  }
  if (canEditDocument) {
    mainActions.push({
      label: getLocalizedText("document.edit"),
      onClick: () => setShowEdit(true),
    });
  }
  if (canDeleteDocument) {
    mainActions.push({
      label: getLocalizedText("document.delete"),
      onClick: () => {
        dispatch({
          type: SHOW_CONFIRM_MODAL,
          payload: {
            title: ts.removeConfirm("document"),
            description: document?.filename,
            showConfirmModal: true,
            actions: [
              {
                label: getLocalizedText("system.cancel"),
                onClick: () => {
                  dispatch({
                    type: SHOW_CONFIRM_MODAL,
                    payload: { showConfirmModal: false },
                  });
                },
                variant: EButtonVariants.Outlined,
              },
              {
                label: getLocalizedText("system.remove"),
                onClick: () => {
                  if (document.templateDocumentId) {
                    dispatch(
                      templateDocumentActions.removeStart.getAction({
                        id: document.templateDocumentId,
                        suppressSuccessAlert: true,
                      }),
                    );
                  }
                  dispatch(
                    documentActions.removeStart.getAction({ id: document.id }),
                  );
                },
              },
            ],
          },
        });
      },
    });
  }

  const renderTags = () => {
    const items: React.ReactNode[] = [];

    items.push(<Tag>{getDocumentCategoryName(document.categoryId)}</Tag>);

    if (document.templateDocumentId) {
      items.push(
        <Tag variant={ETagVariant.Default}>
          {getLocalizedText("document.tag.template_document")}
        </Tag>,
      );
    }

    if (items.length > 0) {
      return (
        <Stack flexDirection="column" gap={1}>
          {items}
        </Stack>
      );
    }
  };

  const renderMeta = () => {
    return (
      <Stack direction="column" gap={1} py={1.25}>
        {renderTags()}
        <Typography variant="h3">{document.filename}</Typography>
        <Stack direction="row" gap={1} alignItems="center">
          {property?.typeId && (
            <Icon
              // @ts-ignore
              path={PropertyIcon[property.typeId]}
              width="16px"
              height="16px"
            />
          )}
          {property && (
            <Typography
              variant="body1"
              sx={sx.content.propertyTitle}
              onClick={() => navigate(`/properties/${document.propertyId}`)}
            >
              {formatAddress(property)}
            </Typography>
          )}
        </Stack>
        {document.createdAt && (
          <Stack direction="row" gap={1} alignItems="center">
            <Icon path={EAssets.CalendarIcon} width="16px" height="16px" />
            <Typography variant="body1">
              {getLocalizedText("system.date.upload")}:{" "}
              {utils.date.format(document.createdAt, "dd/MM/yyyy")}
            </Typography>
          </Stack>
        )}
        {document.filesize && document.filesize > 0 && (
          <Stack direction="row" gap={1} alignItems="center">
            <Icon path={EAssets.PdfIcon} width="16px" height="16px" />
            <Typography variant="body1">
              {formatBytes(document.filesize)}
            </Typography>
          </Stack>
        )}
        {documentTypeData.name && (
          <Stack direction="row" gap={1} alignItems="center">
            <Icon path={EAssets.PdfIcon} width="16px" height="16px" />
            <Typography variant="body1">{documentTypeData.name}</Typography>
          </Stack>
        )}
      </Stack>
    );
  };

  const renderInfo = () => {
    return (
      <DocumentInfo
        document={document}
        linkedProperty={linkedProperty}
        linkedTask={linkedTask}
        mainActions={mainActions}
      />
    );
  };

  return (
    <>
      {renderMeta()}
      {renderInfo()}
      {showEdit && (
        <AddDocumentModal
          openDialog={showEdit}
          handleClose={() => setShowEdit(false)}
          handleEdit={handleEdit}
          file={document}
        />
      )}
    </>
  );
};
